import { createApi } from "@reduxjs/toolkit/query/react"

import { UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export interface AdminMessage {
  id: number
  batch_id: number
  scheduled_at: string | null
  delivered_at?: string | null
  title: string
  content_plain: string
  content_rich?: string | null
  delivery_status: string
}

export interface AdminMessageDevice {
  id: number
  device_name: string | null
  serial_number: string
  contractor_name: string | null
  contractor_account: string | null
  account_type: string | null
  customer_name: string | null
  customer_email: string | null
  device_status: string | null
  messages: AdminMessage[]
}

export interface AdminMessageDevicesResponse {
  data: AdminMessageDevice[]
  meta?: {
    current_page: number
    last_page: number
    per_page: number
    total: number
    from: number
    to: number
  }
}

export interface AdminMessageDevicesParams {
  device_ids: number[]
}

export const serializeAdminMessageDeviceIds = (deviceIds: number[]) => {
  const searchParams = new URLSearchParams()
  deviceIds.forEach(id => searchParams.append("device_ids", String(id)))
  return searchParams.toString()
}

export interface AdminMessageTemplate {
  id: number
  version?: number
  title: string
  content_plain?: string
  content_rich?: string | null
  content_type?: string
  created_at?: string
  updated_at?: string
  repeat_period?: "never" | "1_month" | "6_months" | "12_months" | null
}

export interface AdminMessageDispatchRequest {
  device_ids: number[]
  message_template_id?: number
  title?: string
  content_plain?: string
  content_rich?: string
  scheduled_at?: string
  timezone?: string
}

export interface AdminMessageDispatchResponse {
  id?: number
  batch_id?: number
  status?: string
  message?: string
}

export interface AdminMessageBatch {
  id: number
  title?: string
  status: string
  contentPlain?: string | null
  contentRich?: string | null
  scheduledAt?: string | null
  actualSentAt?: string | null
  createdAt?: string | null
  targetDeviceCount?: number
  deliveredCount?: number
  pendingCount?: number
  failedCount?: number
  message_template_id?: number | null
  scheduled_at?: string | null
  timezone?: string | null
  device_count?: number
  contractor?: string | null
  account_type?: string | null
  created_by?: string | null
  created_at?: string
  repeat?: string | null
  delivered?: number
  pending?: number
  failed?: number
  canceled?: number
}

export interface AdminMessageBatchesResponse {
  data: AdminMessageBatch[]
  meta?: AdminMessageDevicesResponse["meta"]
}

export const adminMessagesApi = createApi({
  reducerPath: "adminMessages",
  tagTypes: ["admin-messages"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getAdminMessageDevices: builder.query<AdminMessageDevicesResponse, AdminMessageDevicesParams>({
      query: ({ device_ids }) => ({
        url: `/admin/messages/devices?${serializeAdminMessageDeviceIds(device_ids)}`,
        method: "GET",
        headers: { Accept: "application/json" },
      }),
      providesTags: ["admin-messages"],
    }),
    getAdminMessageTemplates: builder.query<{ data: AdminMessageTemplate[] }, UITypes.Params | void>({
      query: params => ({
        url: "/admin/messages/templates",
        method: "GET",
        params: prepareQueryParams(params || {}),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["admin-messages"],
    }),
    getAdminMessageTemplate: builder.query<{ data: AdminMessageTemplate }, number>({
      query: id => ({ url: `/admin/messages/templates/${id}`, method: "GET", headers: { Accept: "application/json" } }),
      providesTags: ["admin-messages"],
    }),
    createAdminMessageTemplate: builder.mutation<{ data: AdminMessageTemplate }, Partial<AdminMessageTemplate>>({
      query: body => ({
        url: "/admin/messages/templates",
        method: "POST",
        body: { ...body, version: 1, content_type: "text/markdown" },
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      }),
      invalidatesTags: ["admin-messages"],
    }),
    updateAdminMessageTemplate: builder.mutation<
      { data: AdminMessageTemplate },
      { id: number; body: Partial<AdminMessageTemplate> }
    >({
      query: ({ id, body }) => ({
        url: `/admin/messages/templates/${id}`,
        method: "PUT",
        body: { ...body, content_type: "text/markdown" },
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      }),
      invalidatesTags: ["admin-messages"],
    }),
    deleteAdminMessageTemplate: builder.mutation<void, number>({
      query: id => ({ url: `/admin/messages/templates/${id}`, method: "DELETE" }),
      invalidatesTags: ["admin-messages"],
    }),
    duplicateAdminMessageTemplate: builder.mutation<{ data: AdminMessageTemplate }, number>({
      query: id => ({
        url: `/admin/messages/templates/${id}/duplicate`,
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      }),
      invalidatesTags: ["admin-messages"],
    }),
    getAdminMessageHistory: builder.query<AdminMessageBatchesResponse, UITypes.Params>({
      query: params => ({ url: "/admin/messages/history", method: "GET", params: prepareQueryParams(params) }),
      transformResponse: (response: AdminMessageBatch[] | AdminMessageBatchesResponse) =>
        Array.isArray(response) ? { data: response } : response,
      providesTags: ["admin-messages"],
    }),
    getAdminScheduledMessages: builder.query<AdminMessageBatchesResponse, UITypes.Params>({
      query: params => ({ url: "/admin/messages/scheduled", method: "GET", params: prepareQueryParams(params) }),
      providesTags: ["admin-messages"],
    }),
    getAdminMessage: builder.query<
      { data: AdminMessageBatch & { messages?: AdminMessage[]; devices?: AdminMessageDevice[] } },
      number
    >({
      query: id => ({ url: `/admin/messages/${id}`, method: "GET" }),
      providesTags: ["admin-messages"],
    }),
    sendScheduledAdminMessageNow: builder.mutation<AdminMessageDispatchResponse, number>({
      query: id => ({ url: `/admin/messages/${id}/send-now`, method: "POST" }),
      invalidatesTags: ["admin-messages"],
    }),
    cancelScheduledAdminMessage: builder.mutation<AdminMessageDispatchResponse, number>({
      query: id => ({ url: `/admin/messages/${id}/cancel`, method: "POST" }),
      invalidatesTags: ["admin-messages"],
    }),
    sendAdminMessagesNow: builder.mutation<AdminMessageDispatchResponse, AdminMessageDispatchRequest>({
      query: body => ({
        url: "/admin/messages/send-now",
        method: "POST",
        body,
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      }),
      invalidatesTags: ["admin-messages"],
    }),
    scheduleAdminMessages: builder.mutation<AdminMessageDispatchResponse, AdminMessageDispatchRequest>({
      query: body => ({
        url: "/admin/messages/schedule",
        method: "POST",
        body,
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      }),
      invalidatesTags: ["admin-messages"],
    }),
  }),
})

export const {
  useGetAdminMessageDevicesQuery,
  useLazyGetAdminMessageDevicesQuery,
  useGetAdminMessageTemplatesQuery,
  useLazyGetAdminMessageTemplatesQuery,
  useSendAdminMessagesNowMutation,
  useScheduleAdminMessagesMutation,
  useGetAdminMessageTemplateQuery,
  useCreateAdminMessageTemplateMutation,
  useUpdateAdminMessageTemplateMutation,
  useDeleteAdminMessageTemplateMutation,
  useDuplicateAdminMessageTemplateMutation,
  useGetAdminMessageHistoryQuery,
  useGetAdminScheduledMessagesQuery,
  useGetAdminMessageQuery,
  useSendScheduledAdminMessageNowMutation,
  useCancelScheduledAdminMessageMutation,
} = adminMessagesApi
